
import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from './lib/supabase';
import Login from './components/Login';
import PatientList from './components/patients/PatientList';
import SampleList from './components/SampleList';
import AdminPanel from './components/admin/AdminPanel';
import Sidebar from './components/layout/Sidebar';
import DashboardStats from './components/dashboard/DashboardStats';
import { Menu } from 'lucide-react';

type View = 'dashboard' | 'patients' | 'samples' | 'microbio' | 'admin';

const App: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  
  const [currentView, setCurrentView] = useState<View>(() => {
    const savedView = localStorage.getItem('sil_current_view');
    if (savedView && ['dashboard', 'patients', 'samples', 'microbio', 'admin'].includes(savedView)) {
      return savedView as View;
    }
    return 'dashboard';
  });

  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isSupabaseConfigured()) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setLoading(false);
      });

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        setSession(session);
      });

      return () => subscription.unsubscribe();
    } else {
      const localSession = localStorage.getItem('sil_mock_session');
      if (localSession) {
        setSession(JSON.parse(localSession));
      }
      setLoading(false);
    }
  }, []);

  const handleLogin = () => {
    if (!isSupabaseConfigured()) {
      const mockSession = { 
        user: { 
          email: 'demo@labo.com', 
          role: 'admin',
          user_metadata: { full_name: 'Admin Demo' } 
        } 
      };
      localStorage.setItem('sil_mock_session', JSON.stringify(mockSession));
      setSession(mockSession);
    }
  };

  const handleLogout = async () => {
    if (isSupabaseConfigured()) {
      await supabase.auth.signOut();
    } else {
      localStorage.removeItem('sil_mock_session');
      setSession(null);
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (view: View) => {
    setCurrentView(view);
    localStorage.setItem('sil_current_view', view);
    setIsMobileMenuOpen(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500">Chargement du SIL...</div>;

  if (!session) {
    return <Login onLoginSuccess={handleLogin} />;
  }

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">
      
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <Sidebar 
        currentView={currentView}
        isMobileMenuOpen={isMobileMenuOpen}
        session={session}
        onNavClick={handleNavClick}
        onLogout={handleLogout}
      />

      <main className="flex-1 flex flex-col overflow-hidden w-full">
        <header className="bg-white shadow-sm border-b border-slate-200 h-16 flex items-center justify-between px-4 md:px-8 shrink-0 z-20">
           <div className="flex items-center gap-3">
             <button 
               onClick={() => setIsMobileMenuOpen(true)}
               className="md:hidden text-slate-500 hover:text-slate-800 p-1"
             >
               <Menu size={24} />
             </button>
             <h2 className="text-lg font-semibold text-slate-800 capitalize truncate">
               {currentView === 'dashboard' ? 'Tableau de bord' : currentView === 'admin' ? 'Administration' : currentView}
             </h2>
           </div>
           {!isSupabaseConfigured() && (
             <span className="hidden sm:inline-block text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded border border-orange-200">Mode Démo (Local)</span>
           )}
        </header>

        {/* Le conteneur ici est overflow-hidden, les vues enfants gèrent leur propre scroll */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {currentView === 'dashboard' ? (
            <div className="p-4 md:p-8 overflow-y-auto flex-1">
              <DashboardStats />
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col">
              {currentView === 'patients' && <PatientList />}
              {currentView === 'samples' && <SampleList />}
              {currentView === 'microbio' && <div className="p-12 text-center text-slate-400">Module Microbiologie en construction</div>}
              {currentView === 'admin' && <AdminPanel />}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default App;
